import { useQuery } from "@tanstack/react-query";
import {
  TeamResponse,
  UnauthorizedError,
  fetchTeam,
  formatEUR,
} from "../../lib/api";
import type { HoldingFirma, TeamPerson, TooltimeTeamBlock } from "../../lib/api";
import { FIRMA_COLORS, FIRMA_LABEL } from "../../lib/firma-colors";

const FIRMEN: HoldingFirma[] = ["drainx", "gundk", "infrax", "xholding"];

const EMPTY_MESSAGES: Record<string, string> = {
  no_lohn_data:
    "Keine Lohndaten vorhanden — StB-Strecke (Pahl-IMAP) liefert nicht. Diagnose: Auftrag G.",
  no_active_employees: "Keine aktiven Mitarbeiter erfasst.",
};

function tageSeit(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
}

export default function TeamTabSections() {
  return (
    <div className="space-y-4">
      {FIRMEN.map((f) => (
        <FirmaTeamCard key={f} firma={f} />
      ))}
    </div>
  );
}

function FirmaTeamCard({ firma }: { firma: HoldingFirma }) {
  const q = useQuery<TeamResponse, Error>({
    queryKey: ["team", firma],
    queryFn: () => fetchTeam(firma),
    staleTime: 5 * 60_000,
    retry: (n, err) => !(err instanceof UnauthorizedError) && n < 1,
  });

  // Design-Wurf 03.07.: Karte flaechig in Markenfarbe — linker Vollbalken
  // + getoenter Header statt neutralem Grau.
  const palette = FIRMA_COLORS[firma];
  return (
    <section
      className={`relative overflow-hidden rounded-2xl border ${palette.border} bg-slate-950`}
    >
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1.5 ${palette.accentBar}`} />
      <header
        className={`flex items-baseline justify-between px-4 sm:px-5 py-2.5 ${palette.headerTint}`}
      >
        <h3
          className={`text-[11px] font-semibold uppercase tracking-widest ${palette.text}`}
        >
          {FIRMA_LABEL[firma] ?? firma} · Team
        </h3>
        {q.data?.data_freshness_iso && (
          <span className="text-[10px] text-slate-400">
            Stand{" "}
            {new Date(q.data.data_freshness_iso).toLocaleDateString("de-DE")}
          </span>
        )}
      </header>
      <div className="p-4 sm:p-5 pt-3">

      {q.isLoading && (
        <div className="h-20 animate-pulse rounded bg-slate-800/40" />
      )}

      {q.data?.lohn_stale && (
        <div className="mb-3 rounded-lg border border-rose-500/60 bg-rose-500/10 px-3 py-2">
          <p className="text-xs font-semibold text-rose-200">
            ⛔ Lohndaten eingefroren auf {q.data.lohn_stand_label ?? "altem Stand"}
            {tageSeit(q.data.data_freshness_iso) != null &&
              ` — seit ${tageSeit(q.data.data_freshness_iso)} Tagen nichts Neues`}
          </p>
          <p className="text-[10px] text-rose-200/70 mt-0.5">
            Die Pahl-Strecke (StB-IMAP) liefert nicht. Alle EUR-Werte unten sind
            veraltet. Einsatz-Daten unten kommen live aus Tooltime.
          </p>
        </div>
      )}

      {/* Sanierung 03.07.: 2. Quelle Tooltime — Einsatz-Realität unabhängig
          von der Pahl-Lohnstrecke. J5: die Namensliste kommt gemergt aus
          `personen` (eine Zeile pro Person), hier bleiben nur die KPIs. */}
      {q.data?.tooltime_team && (
        <TooltimeTeamSection
          team={q.data.tooltime_team}
          snapshotIso={q.data.tooltime_snapshot_iso}
          stale={q.data.tooltime_stale}
          hideMitarbeiter={(q.data.personen?.length ?? 0) > 0}
        />
      )}

      {/* AUFTRAG J5: EINE Zeile pro Person — Tooltime-Email ↔ Pahl-Klarname
          im Backend gematcht, Badges für Einzel-Quellen. */}
      {(q.data?.personen?.length ?? 0) > 0 && (
        <PersonenListe personen={q.data!.personen!} />
      )}

      {q.data?.empty_reason && (
        <p className="text-xs text-slate-400">
          {EMPTY_MESSAGES[q.data.empty_reason] ?? q.data.empty_reason}
          {q.data.mitarbeiter_count > 0 && (
            <span className="block mt-1 text-slate-500">
              {q.data.mitarbeiter_aktiv}/{q.data.mitarbeiter_count} Mitarbeiter
              erfasst
            </span>
          )}
        </p>
      )}

      {q.data?.lohn_summen && (
        <>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-3">
            <Kpi label="Brutto YTD" value={formatEUR(q.data.lohn_summen.brutto_ytd_eur)} />
            <Kpi label="Netto YTD" value={formatEUR(q.data.lohn_summen.netto_ytd_eur)} />
            <Kpi label="Brutto MTD" value={formatEUR(q.data.lohn_summen.brutto_mtd_eur)} />
            <Kpi label="Netto MTD" value={formatEUR(q.data.lohn_summen.netto_mtd_eur)} />
          </dl>

          <div className="text-[10px] text-slate-500 mb-2">
            {q.data.mitarbeiter_aktiv}/{q.data.mitarbeiter_count} MA
          </div>

          {/* J5: die Namensliste lebt in PersonenListe — Top-5 nur noch als
              Fallback, wenn das Merge-Feld (alte Deploys) fehlt. */}
          {(q.data.personen?.length ?? 0) === 0 &&
            q.data.top_5_einzelposten.length > 0 && (
              <ul className="space-y-1">
                {q.data.top_5_einzelposten.map((e, i) => (
                  <li
                    key={`${e.mitarbeiter_name}-${i}`}
                    className="flex items-baseline justify-between text-xs gap-2"
                  >
                    <span className="text-slate-200 truncate">
                      {e.mitarbeiter_name}
                    </span>
                    <span className="tabular-nums text-slate-100 shrink-0">
                      {formatEUR(e.brutto_eur)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
        </>
      )}
      </div>
    </section>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="meta-xs">
        {label}
      </dt>
      <dd className="mt-0.5 font-semibold tabular-nums text-slate-100">
        {value}
      </dd>
    </div>
  );
}

// AUFTRAG J5: gemergte Personen-Liste (eine Zeile pro Person). Badge nur
// bei Einzel-Quelle — "beide" ist der Normalfall und bleibt ruhig.
const QUELLE_BADGE: Record<Exclude<TeamPerson["quelle"], "beide">, string> = {
  nur_tooltime: "nur Tooltime",
  nur_lohn: "nur Lohn",
};

function PersonenListe({ personen }: { personen: TeamPerson[] }) {
  return (
    <ul className="space-y-1 mb-3">
      {personen.map((p) => (
        <li
          key={`${p.quelle}-${p.anzeige_name}`}
          className="flex items-baseline justify-between text-xs gap-2"
        >
          <span className="min-w-0 flex items-baseline gap-1.5">
            <span className="text-slate-200 truncate">
              {p.anzeige_name}
              {p.aktiv === false && <span className="text-slate-500"> (inaktiv)</span>}
            </span>
            {p.quelle !== "beide" && (
              <span
                className="shrink-0 rounded-full border border-slate-600/50 bg-slate-800/60 px-1.5 py-px text-[9px] uppercase tracking-wider text-slate-400"
                title={
                  p.quelle === "nur_tooltime"
                    ? "Nur im Tooltime-Snapshot — kein Lohn-Posten im letzten Lohnlauf (z. B. neuer MA oder Funktions-Account)."
                    : "Nur im Pahl-Lohnlauf — kein Tooltime-Eintrag gematcht."
                }
              >
                {QUELLE_BADGE[p.quelle]}
              </span>
            )}
          </span>
          <span className="tabular-nums text-slate-300 shrink-0">
            {p.brutto_letzter_monat_eur != null && formatEUR(p.brutto_letzter_monat_eur)}
            {p.brutto_letzter_monat_eur != null &&
              (p.erfasste_stunden_mtd != null || p.termine_14d_count != null) &&
              " · "}
            {p.erfasste_stunden_mtd != null && `${p.erfasste_stunden_mtd} h`}
            {p.erfasste_stunden_mtd != null && p.termine_14d_count != null && " · "}
            {p.termine_14d_count != null && `${p.termine_14d_count} Termine`}
          </span>
        </li>
      ))}
    </ul>
  );
}

// Sanierung 03.07.: Einsatz-Realität aus dem täglichen Tooltime-Snapshot.
// Rendert nur, was der Pull tatsächlich liefert — keine leeren Deko-Blöcke.
function TooltimeTeamSection({
  team,
  snapshotIso,
  stale,
  hideMitarbeiter,
}: {
  team: TooltimeTeamBlock;
  snapshotIso?: string | null;
  stale?: boolean;
  hideMitarbeiter?: boolean;
}) {
  const kpis: { label: string; value: string }[] = [];
  if (team.mitarbeiter_count != null)
    kpis.push({ label: "MA (Tooltime)", value: String(team.mitarbeiter_count) });
  if (team.termine_next_14d_count != null)
    kpis.push({ label: "Termine 14 T", value: String(team.termine_next_14d_count) });
  if (team.auftraege_offen_count != null)
    kpis.push({ label: "Offene Aufträge", value: String(team.auftraege_offen_count) });
  if (team.erfasste_stunden_mtd_total != null)
    kpis.push({ label: "Stunden MTD", value: `${team.erfasste_stunden_mtd_total} h` });

  const mitarbeiter = hideMitarbeiter ? [] : (team.mitarbeiter ?? []).slice(0, 8);
  if (kpis.length === 0 && mitarbeiter.length === 0) return null;

  return (
    <div className="mb-3 rounded-lg border border-slate-700/50 bg-slate-900/50 px-3 py-2.5">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-sky-300/90">
          Einsatz live · Tooltime
        </span>
        {snapshotIso && (
          <span
            className={`text-[10px] tabular-nums ${stale ? "text-amber-300" : "text-slate-500"}`}
          >
            Stand {new Date(snapshotIso).toLocaleDateString("de-DE")}
            {stale ? " ⚠ alt" : ""}
          </span>
        )}
      </div>

      {kpis.length > 0 && (
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-2">
          {kpis.map((k) => (
            <Kpi key={k.label} label={k.label} value={k.value} />
          ))}
        </dl>
      )}

      {mitarbeiter.length > 0 && (
        <ul className="space-y-1">
          {mitarbeiter.map((m) => (
            <li
              key={m.name}
              className="flex items-baseline justify-between text-xs gap-2"
            >
              <span className="text-slate-200 truncate">
                {m.name}
                {m.aktiv === false && (
                  <span className="text-slate-500"> (inaktiv)</span>
                )}
              </span>
              <span className="tabular-nums text-slate-400 shrink-0">
                {m.erfasste_stunden_mtd != null && `${m.erfasste_stunden_mtd} h`}
                {m.erfasste_stunden_mtd != null && m.termine_14d_count != null && " · "}
                {m.termine_14d_count != null && `${m.termine_14d_count} Termine`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
